import { Link } from "@tanstack/react-router";
import type { StepView } from "@/app/queries";
import { count } from "@/lib/plural";
import { extraDone, extraTasks, unknownQuiz, withCode } from "../PracticePage";

const SHOWN = 3;

/** «Пока ждёшь ментора» (S1, правая колонка под треками): доп. задачи
 *  текущего шага и вопросы «Проверь себя» без «знаю». Честно — вне гейта.
 *  Нечего предложить — виджета нет (это не раздел, пустым он не нужен).
 *  compact — одна ссылка для S8. */
export function WaitingWidget({ current, compact = false }: { current?: StepView; compact?: boolean }) {
  if (!current) return null;
  const tasks = extraTasks(current);
  const quiz = unknownQuiz(current);
  if (tasks.length === 0 && quiz.length === 0) return null;

  if (compact) {
    const what = tasks.length > 0 ? count(tasks.length, "задача", "задачи", "задач") : count(quiz.length, "вопрос", "вопроса", "вопросов");
    return (
      <Link to="/practice" data-testid="waiting-link" className="flex items-center justify-between gap-3 rounded-md border border-dashed border-bd2 bg-card px-3 py-2.5 no-underline">
        <span className="text-[12.5px] text-ink">доп. практика · вне гейта</span>
        <span className="flex-none font-mono text-[11px] text-ink2">{what} →</span>
      </Link>
    );
  }

  const done = extraDone(current);
  const ticked = new Set(current.ticks.extra ?? []);
  const rest = tasks.length - SHOWN;
  const lead =
    current.gate_status === "submitted"
      ? `Гейт шага «${current.title}» у ментора. Пока он смотрит — задачи сверх гейта:`
      : `Задачи сверх гейта шага «${current.title}» — на гейт и прогресс не влияют.`;
  return (
    <section className="flex flex-col gap-3" aria-labelledby="waiting-title" data-testid="waiting-widget">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="waiting-title" className="m-0 font-mono text-[13px] font-semibold uppercase tracking-[.04em] text-mut">
          пока ждёшь ментора
        </h2>
        {tasks.length > 0 && (
          <span className="font-mono text-[11px] text-mut" data-testid="waiting-count">
            сделано {done} из {tasks.length}
          </span>
        )}
      </div>
      <div className="overflow-hidden rounded-md border border-dashed border-bd2 bg-card">
        <p className="m-0 px-3.5 pb-2 pt-[11px] text-[12.5px] leading-[1.45] text-ink2 text-pretty">{lead}</p>
        {tasks.length > 0 && (
          <ul className="m-0 list-none px-3.5 pb-2 pt-0">
            {tasks.slice(0, SHOWN).map((t) => {
              const on = ticked.has(t.id);
              return (
                <li key={t.id} className="grid grid-cols-[14px_minmax(0,1fr)] gap-2 py-1">
                  <span aria-hidden className={`font-mono text-[12px] ${on ? "text-ok" : "text-mut"}`}>
                    {on ? "✓" : "○"}
                  </span>
                  <span className={`line-clamp-2 text-[12.5px] leading-[1.45] ${on ? "text-mut" : "text-ink"}`}>{withCode(t.text)}</span>
                </li>
              );
            })}
          </ul>
        )}
        {rest > 0 && <div className="px-3.5 pb-2 font-mono text-[11px] text-mut">и ещё {count(rest, "задача", "задачи", "задач")}</div>}
        {quiz.length > 0 && (
          <div className="border-t border-bd px-3.5 py-2 text-[12px] leading-[1.45] text-ink2" data-testid="waiting-quiz-line">
            ещё {count(quiz.length, "вопрос", "вопроса", "вопросов")} «Проверь себя» без отметки «знаю»
          </div>
        )}
        <Link to="/practice" className="block border-t border-bd px-3.5 py-2.5 font-mono text-[11.5px] text-acc no-underline hover:underline">
          вся доп. практика →
        </Link>
      </div>
    </section>
  );
}
